import { useMemo } from 'react';
import {
  resolveSiteContent,
  type ResolvedSiteContent,
  type SiteManifest,
} from '@mlm/shared';
import { storefrontAssetUrl } from '../../lib/api';
import { useStorefrontContent } from './hooks';

/** URL or absolute public path — render directly; anything else is an R2 key. */
const isDirectSrc = (v: string) => /^(https?:\/\/|\/)/i.test(v);

export type SiteContent = ResolvedSiteContent & {
  /** Resolved image src for a manifest image FIELD: uploaded key wins, else the baked default. */
  img(path: string): string;
  /** Resolved image src for a list-row VALUE (e.g. a gallery slide's imageKey). */
  src(value: string): string;
};

/**
 * Bespoke-site content accessor: manifest defaults ⟵ the admin "Site Pages"
 * overlay riding `GET /storefront/content`. First paint uses the defaults
 * (identical to the old hardcoded copy) and swaps in edits when the query
 * lands — same pattern as the CMS-backed footer/testimonials.
 */
export function useSiteContent(manifest: SiteManifest): SiteContent {
  const { data } = useStorefrontContent();
  const overlay = data.siteContent;
  return useMemo(() => {
    const r = resolveSiteContent(manifest, overlay);
    const src = (value: string) => (isDirectSrc(value) ? value : storefrontAssetUrl(value));
    return {
      ...r,
      src,
      img: (path: string) => {
        const k = r.imageKey(path);
        return k ? src(k) : r.imageDefault(path);
      },
    };
  }, [manifest, overlay]);
}
